import { ArrowsClockwise, ChartLineUp, Crown, Lightning, Receipt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import useAdminLoad from "@/hooks/useAdminLoad";
import { LoadState, PageHeader, Panel, Pill } from "@/components/admin/AdminUI";
import LaunchTiles from "@/components/admin/LaunchTiles";
import { formatDate, humanize } from "@/lib/format";

const usd = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Tile({ icon: Icon, title, value, sub, mock, testId, children }) {
  return (
    <Panel className="flex flex-col p-6" data-testid={testId}>
      <div className="flex items-center gap-2"><Icon size={18} className="cw-brand-text" aria-hidden /><p className="cw-soft text-sm">{title}</p></div>
      <p className="mt-4 text-4xl font-semibold tabular-nums tracking-tight" data-testid={`${testId}-value`}>{value}</p>
      {sub && <p className="cw-soft mt-1 text-sm" data-testid={`${testId}-sub`}>{sub}</p>}
      {children}
      {mock && <p className="cw-muted mt-auto border-t border-border pt-3 text-xs" data-testid={`${testId}-mock`}>Test: {mock}</p>}
    </Panel>
  );
}

function Breakdown({ title, map, testId }) {
  const entries = Object.entries(map || {});
  return (
    <div className="mt-3" data-testid={testId}>
      <p className="cw-muted text-xs">{title}</p>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {entries.length ? entries.map(([k, v]) => <Pill key={k} className="capitalize">{humanize(k)} {v}</Pill>) : <span className="cw-muted text-xs">None</span>}
      </div>
    </div>
  );
}

const countLine = (x, unit) => `${x?.count ?? 0} ${x?.count === 1 ? unit.replace(/s$/, "") : unit}${x?.quantity ? ` · ${x.quantity} Super Crushes` : ""} · ${usd(x?.gross_usd)}`;

export default function Dashboard() {
  const { data: d, error, loading, reload: reloadRevenue } = useAdminLoad("/admin/dashboard/revenue");
  const launch = useAdminLoad("/admin/dashboard");
  const reload = () => { reloadRevenue(); launch.reload(); };
  const subs = d?.active_subscriptions;
  return (
    <div data-testid="admin-dashboard-page">
      <PageHeader title="Dashboard" description="Launch, community and revenue at a glance. Real accounts only; test data is shown separately and muted."
        actions={<Button variant="outline" onClick={reload} data-testid="dashboard-refresh-button"><ArrowsClockwise />Refresh</Button>} />
      <LoadState loading={launch.loading && !launch.data} error={launch.error} onRetry={launch.reload}>
        {launch.data && <LaunchTiles d={launch.data} />}
      </LoadState>
      <h2 className="mb-4 mt-10 text-lg font-semibold">Revenue</h2>
      <LoadState loading={loading && !d} error={error} onRetry={reloadRevenue}>
        {d && (
          <>
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
              <Tile icon={Crown} title="Active subscriptions" value={subs?.total ?? 0} testId="dashboard-active-subs">
                <Breakdown title="By tier" map={subs?.by_tier} testId="dashboard-subs-by-tier" />
                <Breakdown title="By store" map={subs?.by_store} testId="dashboard-subs-by-store" />
                <Breakdown title="By state" map={subs?.by_state} testId="dashboard-subs-by-state" />
              </Tile>
              <Tile icon={Lightning} title="Super Crush packs sold" value={d.packs_sold?.real?.count ?? 0} testId="dashboard-packs"
                sub={countLine(d.packs_sold?.real, "packs")} mock={countLine((d.packs_sold?.test ?? d.packs_sold?.mock), "packs")} />
              <Tile icon={Receipt} title="Subscription payments" value={d.subscription_payments?.real?.count ?? 0} testId="dashboard-sub-payments"
                sub={usd(d.subscription_payments?.real?.gross_usd)} mock={countLine((d.subscription_payments?.test ?? d.subscription_payments?.mock), "payments")} />
              <Tile icon={ChartLineUp} title={`Gross revenue (${d.currency || "USD"})`} value={usd(d.gross_revenue_usd?.real)} testId="dashboard-gross"
                sub="Before store fees and refunds" mock={usd((d.gross_revenue_usd?.test ?? d.gross_revenue_usd?.mock))} />
            </div>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-sm">
              <p className="cw-muted" data-testid="dashboard-revenuecat">
                {d.revenuecat_metrics ? "RevenueCat metrics connected." : "RevenueCat metrics: connect in a later step"}
              </p>
              <p className="cw-muted text-xs" data-testid="dashboard-as-of">As of {formatDate(d.as_of)}</p>
            </div>
          </>
        )}
      </LoadState>
    </div>
  );
}
